import type { TFunction } from 'i18next';
import type {
  ZendeskDraftStatus,
  ZendeskDraftSummary,
  ZendeskEditReason,
  ZendeskReviewOutcome,
  ZendeskReviewStatus,
  ZendeskSkipReason,
} from '@mygpt/app/apis/services/zendesk';

export const CARD_SHADOW = '0px 10px 60px 0px rgba(226, 236, 249, 0.50)';
export const ACCENT = '#03363D'; // Zendesk 品牌色，与連携アプリ卡一致

export const EDIT_REASONS: ZendeskEditReason[] = ['knowledge_gap', 'case_specific', 'tone', 'policy', 'out_of_scope'];
export const REVIEW_OUTCOMES: ZendeskReviewOutcome[] = [
  'proposed',
  'wording_only',
  'case_specific',
  'duplicate',
  'gap_logged',
  'no_action',
];

// ナレッジ不足で未生成（旧 unanswerable も同じ意味）
export const isKnowledgeGap = (skip?: ZendeskSkipReason | null) => skip === 'knowledge_gap' || skip === 'unanswerable';

// 判定 badge 配色：直发=绿 / 编辑=橙 / 自作=红 / 未判定=蓝 / 其他=灰
export const statusColor = (status: ZendeskDraftStatus): string => {
  switch (status) {
    case 'answered_as_is':
      return 'teal';
    case 'answered_edited':
      return 'orange';
    case 'answered_self':
      return 'red';
    case 'posted':
    case 'inserted':
    case 'generating':
      return 'blue';
    case 'dry_run':
      return 'grape';
    default:
      return 'gray';
  }
};

export const statusLabel = (t: TFunction, status: ZendeskDraftStatus, skipReason?: ZendeskSkipReason | null) => {
  const base = t(`zendesk_ops.status.${status}`);
  if (status === 'skipped' && skipReason) return `${base}（${t(`zendesk_ops.skip.${skipReason}`)}）`;
  return base;
};

export const reasonLabel = (t: TFunction, reason?: ZendeskEditReason | string | null) =>
  reason ? t(`zendesk_ops.reason.${reason}`) : '';

// 対応状況：编辑/自作 と ナレッジ不足 が運営の改善タスク；直发=対応不要、その他の跳过/破棄=対象外
export type ReviewDisplay = ZendeskReviewStatus | 'not_needed' | 'out_of_scope' | 'waiting';

export const reviewDisplay = (
  d: Pick<ZendeskDraftSummary, 'status' | 'review_status'> & { skip_reason?: ZendeskSkipReason | null }
): ReviewDisplay => {
  if (needsReview(d.status, d.skip_reason)) return d.review_status || 'pending';
  if (d.status === 'answered_as_is') return 'not_needed';
  if (d.status === 'posted' || d.status === 'inserted' || d.status === 'generating') return 'waiting';
  return 'out_of_scope';
};

export const reviewColor = (r: ReviewDisplay): string => {
  switch (r) {
    case 'pending':
      return 'red';
    case 'in_progress':
      return 'yellow';
    case 'done':
      return 'teal';
    case 'waiting':
      return 'blue';
    default:
      return 'gray';
  }
};

export const needsReview = (status: ZendeskDraftStatus, skipReason?: ZendeskSkipReason | null) =>
  status === 'answered_edited' || status === 'answered_self' || (status === 'skipped' && isKnowledgeGap(skipReason));

export const formatSimilarity = (sim?: number | null) => (sim === null || sim === undefined ? '—' : sim.toFixed(2));

export const formatPercent = (rate?: number | null) =>
  rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`;

export const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const sameYear = d.getFullYear() === today.getFullYear();
  const date = d.toLocaleDateString([], sameYear ? { month: '2-digit', day: '2-digit' } : undefined);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
};

// 字符级 LCS diff（草稿 vs 最终回答都很短，O(n·m) 足够）；超长截断避免卡 UI
export type DiffSeg = { text: string; type: 'equal' | 'del' | 'ins' };

const DIFF_CAP = 2500;

export const charDiff = (a: string, b: string): { draft: DiffSeg[]; final: DiffSeg[] } => {
  const x = Array.from((a || '').slice(0, DIFF_CAP));
  const y = Array.from((b || '').slice(0, DIFF_CAP));
  const n = x.length;
  const m = y.length;
  if (!n || !m) {
    return {
      draft: n ? [{ text: x.join(''), type: 'del' }] : [],
      final: m ? [{ text: y.join(''), type: 'ins' }] : [],
    };
  }
  // dp[i][j] = LCS length of x[i:], y[j:]，用一维滚动数组的话回溯不便，这里直接二维（n,m ≤ 2500）
  const dp: Uint16Array[] = new Array(n + 1);
  for (let i = 0; i <= n; i++) dp[i] = new Uint16Array(m + 1);
  for (let i = n - 1; i >= 0; i--) {
    const row = dp[i];
    const next = dp[i + 1];
    for (let j = m - 1; j >= 0; j--) {
      row[j] = x[i] === y[j] ? next[j + 1] + 1 : Math.max(next[j], row[j + 1]);
    }
  }
  const draft: DiffSeg[] = [];
  const final: DiffSeg[] = [];
  const push = (arr: DiffSeg[], ch: string, type: DiffSeg['type']) => {
    const last = arr[arr.length - 1];
    if (last && last.type === type) last.text += ch;
    else arr.push({ text: ch, type });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      push(draft, x[i], 'equal');
      push(final, y[j], 'equal');
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push(draft, x[i], 'del');
      i++;
    } else {
      push(final, y[j], 'ins');
      j++;
    }
  }
  while (i < n) push(draft, x[i++], 'del');
  while (j < m) push(final, y[j++], 'ins');
  return { draft, final };
};
